import React, { useState, useEffect } from "react";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "components/ui/command";
import useDebounce from "../UseDebounce/useDebounce";
import FiltersSelectedTags from "../FilterSelectedTags/FilterSelectedTags";
import { Loader2, ChevronDown, Check } from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";

export const ShadcnAutoCompleteSearch = ({
  placeholder,
  functionSearch = () => {},
  filters,
  setFilters,
  entity,
  callback = null,
  hideFilterSelectedTags,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const debouncedValue = useDebounce(query); // using default delay

  useEffect(() => {
    let searchController;
    if (debouncedValue && debouncedValue.length > 2) {
      if (searchController) searchController.abort();
      setLoading(true);
      searchController = functionSearch(debouncedValue, (res) => {
        setOptions(
          res.map((item) => ({
            ...item,
            value: item.name ? item.name : item.value,
            name: item.name ? item.name : item.value,
          }))
        );
        setLoading(false);
      });
    } else {
      setOptions([]);
    }
    return () => {
      if (searchController) searchController.abort();
    };
  }, [debouncedValue, functionSearch]);

  const handleSelect = (value, name) => {
    setFilters({
      ...filters,
      [`${entity}`]: name,
    });
    setOpen(false);
    if (callback) callback();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setFilters({ ...filters, [`${entity}`]: query });
      setOpen(false);
      if (callback) callback();
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="relative w-full">
        <Command shouldFilter={false} className="border border-input rounded-md shadow-sm overflow-visible bg-white">
          <PopoverPrimitive.Root open={open && query.length > 0} onOpenChange={(val) => { if (!val) setOpen(false); }}>
            <PopoverPrimitive.Anchor asChild>
              <div className="w-full">
                <CommandInput 
                  placeholder={placeholder} 
                  value={query}
                  onValueChange={(val) => {
                    setQuery(val);
                    setOpen(!!val);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => { if(query) setOpen(true) }}
                  onBlur={() => setTimeout(() => setOpen(false), 200)}
                  className="h-9 text-sm border-none ring-0 focus:ring-0 shadow-none bg-transparent placeholder:text-gray-400"
                />
              </div>
            </PopoverPrimitive.Anchor>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                className="z-[1001] w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
              >
                <CommandList className="max-h-60">
                  {isLoading ? (
                    <div className="p-4 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin opacity-50" />
                    </div>
                  ) : (
                    <>
                      {options.length === 0 && <CommandEmpty>No results found.</CommandEmpty>}
                      {options.length > 0 && (
                        <CommandGroup>
                          {options.map((opt) => (
                            <CommandItem
                              key={opt.value}
                              value={opt.value}
                              onSelect={() => handleSelect(opt.value, opt.name)}
                              className="cursor-pointer"
                            >
                              {opt.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </>
                  )}
                </CommandList>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </Command>
      </div>
      {!hideFilterSelectedTags && (
        <FiltersSelectedTags
          filters={{ [entity]: filters[entity] }}
          setFilters={setFilters}
        />
      )}
    </div>
  );
};

export const ShadcnAutoCompleteFilters = ({
  placeholder,
  functionSearch,
  valueField,
  localStorageKey,
  filters,
  setFilters,
  entity,
  getFirstItemOnEnter = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const debouncedValue = useDebounce(query);

  useEffect(() => {
    let searchController;
    if (debouncedValue && debouncedValue.length > 2) {
      if (searchController) searchController.abort();
      setLoading(true);
      searchController = functionSearch(debouncedValue, (res) => {
        setOptions(
          res.map((item) => ({
            ...item,
            value: !!valueField
              ? (item[valueField] || item.name || item.value)
              : item.name
              ? item.name
              : item.value,
            label: item.name ? item.name : item.value,
          }))
        );
        setLoading(false);
      });
    } else {
      setOptions([]);
    }
    return () => {
      if (searchController) searchController.abort();
    };
  }, [debouncedValue, functionSearch, valueField]);

  const handleSelect = (option) => {
    if (typeof localStorageKey === "string") {
      const map = JSON.parse(localStorage.getItem(localStorageKey)) || {};
      if (!map[option.value]) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            ...map,
            [option.value]: option.label,
          })
        );
      }
    }

    setFilters({
      ...filters,
      [`${entity}`]: filters[`${entity}`]
        ? Array.from(new Set([...filters[`${entity}`], `${option.value}`]))
        : [`${option.value}`],
    });
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (!getFirstItemOnEnter) {
        handleSelect({ value: query, label: query });
      } else if (options.length > 0 && !isLoading) {
        handleSelect(options[0]);
      }
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="relative w-full">
        <Command shouldFilter={false} className="border border-input rounded-md shadow-sm overflow-visible bg-white">
          <PopoverPrimitive.Root open={open && query.length > 0} onOpenChange={(val) => { if (!val) setOpen(false); }}>
            <PopoverPrimitive.Anchor asChild>
              <div className="w-full">
                <CommandInput 
                  placeholder={placeholder} 
                  value={query}
                  onValueChange={(val) => {
                    setQuery(val);
                    setOpen(!!val);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => { if(query) setOpen(true) }}
                  onBlur={() => setTimeout(() => setOpen(false), 200)}
                  className="h-9 text-sm border-none ring-0 focus:ring-0 shadow-none bg-transparent placeholder:text-gray-400"
                />
              </div>
            </PopoverPrimitive.Anchor>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                className="z-[1001] w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
              >
                <CommandList className="max-h-60">
                  {isLoading ? (
                    <div className="p-4 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin opacity-50" />
                    </div>
                  ) : (
                    <>
                      {options.length === 0 && <CommandEmpty>No results found.</CommandEmpty>}
                      {options.length > 0 && (
                        <CommandGroup>
                          {options.map((opt) => (
                            <CommandItem
                              key={opt.value}
                              value={opt.value}
                              onSelect={() => handleSelect(opt)}
                              className="cursor-pointer"
                            >
                              {opt.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </>
                  )}
                </CommandList>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </Command>
      </div>
      <FiltersSelectedTags
        filters={{ [entity]: filters[entity] }}
        setFilters={setFilters}
      />
    </div>
  );
};

export const ShadcnSelectListFilters = ({
  placeholder,
  options,
  filters,
  setFilters,
  entity,
}) => {
  const [open, setOpen] = useState(false);
  const selectedValues = filters[entity] || [];

  const handleSelect = (currentValue) => {
    let newSelected = [...selectedValues];
    if (newSelected.includes(currentValue)) {
      newSelected = newSelected.filter((val) => val !== currentValue);
    } else {
      newSelected.push(currentValue);
    }

    setFilters({
      ...filters,
      [entity]: newSelected.length > 0 ? newSelected : null,
    });
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-9 px-3 bg-white hover:bg-gray-50 text-gray-400"
          >
            {placeholder}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search...`} className="placeholder:text-gray-400" />
            <CommandList className="max-h-60">
              <CommandEmpty>No result found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.rawLabel || option.label}
                    disabled={option.disabled}
                    onSelect={() => handleSelect(option.value)}
                    className={cn("flex items-center", option.disabled && "data-[disabled=true]:opacity-100")}
                    style={{ paddingLeft: option.level !== undefined ? `${(option.level * 16) + 8}px` : undefined }}
                  >
                    {!option.disabled && (
                      <div className={cn("mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0", selectedValues.includes(option.value) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible")}>
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    {option.isHeader ? (
                      <strong className="text-gray-400 font-semibold">{option.label}</strong>
                    ) : (
                      <span>{option.label}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      <FiltersSelectedTags
        filters={{ [entity]: filters[entity] }}
        setFilters={setFilters}
      />
    </div>
  );
};
